<template>
  <div class="index-panel">
    <align
      v-show="loaded"
      small-gap
      flex
      class="header"
    >
      <align>
        <index-image :index="index" />

        <div>
          <h1 class="secondary-font">
            {{ index.label }}
          </h1>
          <p v-if="index.description">
            {{ index.description }}
          </p>
        </div>
      </align>

      <icon-button
        v-if="index.custom"
        icon="delete"
        tooltip="Delete Custom Index"
        @click="deleteCustomIndex"
      />

      <icon-button
        v-else-if="isStaff && isPlayerIndex"
        icon="refresh"
        tooltip="Refresh Index"
        :loading="refreshing"
        @click="refreshIndex"
      />
    </align>

    <template v-if="loaded">
      <div class="tabs">
        <tab :active="display === 'stats'" @click="display = 'stats'">
          Stats
        </tab>

        <tab :active="display === 'cards'" @click="display = 'cards'">
          Cards
        </tab>
      </div>

      <div class="wrapper">
        <index-stats-view
          v-if="display === 'stats'"
          :index="index"
        />

        <index-cards-view
          v-show="display === 'cards'"
          :active="display === 'cards'"
          :index-filters="filters"
        />
      </div>
    </template>

    <spinner v-else />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';
import moment from 'moment';

import { indexService, authService } from '@/services';
import { getFilterDescription } from '@cardladder/shared/utils/ladder.util';

import IndexImage from '@cardladder/shared/components/IndexImage';

import IndexCardsView from './IndexCardsView';
import IndexStatsView from './IndexStatsView';

export default {
  components: {
    IndexCardsView,
    IndexImage,
    IndexStatsView,
  },

  props: {
    collection: String,
    doc: String,
  },

  data() {
    return {
      index: {},
      filters: {},
      loading: false,
      loaded: false,
      refreshing: false,
      display: 'stats',
    };
  },

  created() {
    this.load();
  },

  watch: {
    doc(to, from) {
      if (to !== from) {
        this.load();
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    ...mapGetters({
      isStaff: 'isStaff',
    }),

    isPlayerIndex() {
      return this.collection === 'players';
    },
  },

  methods: {
    async load() {
      this.loaded = false;

      try {
        this.index = await indexService.getIndex(this.collection, this.doc);

        if (!this.index.date) {
          this.$router.replace('/indexes');
        }

        if (this.index.config?.cardIds) {
          this.filters = { ids: this.index.config.cardIds };
        } else if (this.index.config?.filters) {
          this.filters = this.index.config.filters;
        }

        if (this.index.custom) {
          this.index.description = getFilterDescription(this.filters);
        }
      } catch (e) {
        this.$toasted.error('Error loading the index');
        Sentry.captureException(e);
      }

      this.loaded = true;
    },

    async deleteCustomIndex() {
      if (window.confirm('Are you sure you want to delete this custom index?')) {
        try {
          await authService.deleteCustomIndex(this.user.uid, this.index.id);

          this.$toasted.success('Deleted your custom index');
          this.$router.push('/indexes');
        } catch (e) {
          this.$toasted.error('Error creating your index');
          Sentry.captureException(e);
        }
      }
    },

    async refreshIndex() {
      this.refreshing = true;

      try {
        const update = {
          config: { filters: { playerIndexId: this.doc }, icon: 'account_circle' },
          clauses: [{ left: 'playerIndexId', middle: '==', right: this.doc }],
          collection: 'players',
          document: this.doc,
          dailyKey: moment().utcOffset(-7).subtract(1, 'days').startOf('day').format('MM/DD/YYYY'),
          field: 'player',
          id: this.doc,
          label: this.doc,
        };

        await firebase.firestore().collection('indexUpdates').doc(update.id).set(update);

        this.$toasted.success('Index refreshed started');
      } catch (e) {
        this.$toasted.error('Error refreshing index');
        Sentry.captureException(e);
      }

      this.refreshing = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.index-panel {
  @include container;
  min-height: 100%;
}

.tabs {
  margin-bottom: $gp;
}

.header {
  margin-bottom: $gp;
  --index-image-size: 80px;

  @media($medium-down) {
    --index-image-size: 48px;
  }

  h1 {
    margin-bottom: 0;
  }

  p {
    color: $color-gray-4;
    font-style: italic;
    font-family: $font-family-secondary;
  }
}
</style>
